import { useState } from 'react';
import { Megaphone } from 'lucide-react';
import { Modal } from '../../shared/ui/Modal';
import { Button } from '../../shared/ui/Button';
import { useToast } from '../../shared/hooks/useToast';
import { useAnnouncements } from './useAnnouncements';

export type AnnouncementsBellProps = { buttonClassName?: string; badgeClassName?: string };

const fmt = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

export function AnnouncementsBell({ buttonClassName, badgeClassName }: AnnouncementsBellProps) {
  const { items, unread, markRead, acknowledge } = useAnnouncements();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;

  async function run(ids: string[]): Promise<void> {
    try {
      await markRead(ids);
    } catch (e) {
      console.error('[announcements] markRead', e);
      toast.error('Não foi possível marcar como lido. Tente novamente.');
    }
  }

  function openList(): void {
    setOpen(true);
    void run(unread.filter((a) => !a.urgent).map((a) => a.id));
  }

  async function entendi(id: string): Promise<void> {
    try {
      await acknowledge(id);
    } catch (e) {
      console.error('[announcements] acknowledge', e);
      toast.error('Não foi possível registrar o "Entendi". Tente novamente.');
    }
  }

  const n = unread.length;
  return (
    <>
      <button
        type="button"
        className={buttonClassName}
        onClick={openList}
        aria-label={`Comunicados (${n} não lidos)`}
        title="Comunicados"
      >
        <Megaphone size={20} />
        {n > 0 ? <span className={badgeClassName}>{n > 9 ? '9+' : n}</span> : null}
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Comunicados do Suporte Ardix" size="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {items.map((a) => (
            <article key={a.id}>
              <h3 style={{ margin: 0 }}>
                {a.title}
                {a.urgent ? <strong style={{ marginLeft: 8, color: 'var(--color-error)' }}>Urgente</strong> : null}
              </h3>
              <small>{fmt(a.publishedAt)}</small>
              <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{a.body}</p>
              {a.urgent && unread.some((u) => u.id === a.id) ? (
                <Button size="sm" onClick={() => void entendi(a.id)}>Entendi</Button>
              ) : null}
            </article>
          ))}
        </div>
      </Modal>
    </>
  );
}
